import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';

import { getAcademicDegrees, getAcademicRanks } from 'utils/func';
import { getBySearchEmail } from 'apis/contacts.api';
import { Combobox, Select, TextInput, TextareaInput } from 'components/FormControl';
import { TOAST } from 'constant';
import { toast } from 'layout';
import useDebounce from 'utils/hooks/useDebounce';
import { updateGenderById } from 'apis/employees.api';
import { catchError } from 'utils/func/error';
import { membersValidationSchema } from '../../validation';

const formOptions = { resolver: yupResolver(membersValidationSchema) };

const MemberDialog = forwardRef(
  ({ organizations, roles, curMembers, projectProposalMembers, councilTypeSelected }, ref) => {
    const { t } = useTranslation();
    const [queryEmail, setQueryEmail] = useState('');
    const queryDebounceEmail = useDebounce(queryEmail, 200);
    const [contacts, setContacts] = useState([]);
    const [memberControl, setMemberControl] = useState();
    const [visible, setVisible] = useState(false);
    const [isCreateNewMember, setIsCreateNewMember] = useState(false);

    const { data: contactsData } = useQuery(
      ['contacts', queryDebounceEmail],
      () => getBySearchEmail(queryDebounceEmail),
      {
        staleTime: 0,
      }
    );

    const {
      control,
      watch,
      setValue,
      reset,
      trigger,
      formState: { errors, dirtyFields },
    } = useForm({ ...formOptions, mode: 'onChange' });

    const organizationId = watch('organizationId');

    const currentOrganization = useMemo(() => {
      if (organizations?.length) {
        const result = organizations?.find((item) => item?.id === organizationId) ?? {};
        setValue('organizationEnglishName', result?.englishName ?? '');
        if (result?.englishName !== 'Other') {
          setValue('specificOrganizationName', null);
        }
        return result;
      }
      return null;
    }, [organizations, organizationId]);
    // #region Event

    const search = (e) => {
      if (e.query) setQueryEmail(e.query);
    };

    useEffect(() => {
      if (contactsData?.data) {
        setContacts(contactsData?.data);
      }
    }, [contactsData]);

    const genders = [
      { value: 'male', name: t('gender.male') },
      { value: 'female', name: t('gender.female') },
    ];

    useImperativeHandle(
      ref,
      () => ({
        open: (_memberControl) => {
          setMemberControl(_memberControl);
          setVisible(true);
          if (_memberControl.type === 'APPEND') {
            reset({});
          } else if (_memberControl.type === 'UPDATE') {
            const value = { ..._memberControl.value };
            const organization =
              organizations?.find((item) => item?.id === value?.contact?.organizationId) ?? {};

            if (organization?.englishName === 'Other') {
              value.specificOrganizationName = value?.contact?.specificOrganizationName;
            }

            value.gender = value?.contact?.scientificProfile?.gender || '';

            reset(value);
          }
        },
      }),
      [organizations]
    );

    const handleAddMember = async () => {
      const isValidTrigger = await trigger();
      if (!isValidTrigger) {
        toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
        return;
      }

      const data = watch();
      const { append, update, index, type } = memberControl;

      const isEmail = curMembers.find((member) => member.email === (data.email ?? data?.contact));
      if (isEmail) {
        toast(TOAST.ERROR, t('council.member.validationDuplicateMember'));
        return;
      }

      if (councilTypeSelected !== 'preliminarySelectionCouncil') {
        const isExistedMemberInProjectProposal = projectProposalMembers.find(
          (member) => member.email === (data.email ?? data?.contact)
        );
        if (isExistedMemberInProjectProposal) {
          toast(TOAST.ERROR, t('council.member.validationExistedProjectProposalMember'));
          return;
        }
      }

      if (data?.contact?.userId && data.gender) {
        try {
          await updateGenderById({ id: data.contact.userId, body: { gender: data.gender } });
          //   toast(TOAST.SUCCESS, t('council.member.updateGenderSuccess'));
        } catch (error) {
          catchError(t, error);
          return;
        }
      }

      if (type === 'APPEND' && append) {
        append({
          email: data.email ?? data?.contact,
          contact: data?.contact,
          fullname: data.fullname,
          gender: data.gender,
          academicRank: data?.academicRank,
          academicDegree: data?.academicDegree,
          roleId: data.roleId,
          organizationId: data.organizationId,
          specificOrganizationName: data?.specificOrganizationName,
        });
      } else if (type === 'UPDATE' && update) {
        update(index, {
          email: data.email ?? data?.contact,
          contact: data?.contact,
          fullname: data.fullname,
          gender: data.gender,
          academicRank: data?.academicRank,
          academicDegree: data?.academicDegree,
          roleId: data.roleId,
          organizationId: data.organizationId,
          specificOrganizationName: data?.specificOrganizationName,
        });
      }
      setIsCreateNewMember(false);
      reset({});
      setVisible(false);
    };

    const handleEmailSelect = (e, value) => {
      setValue('email', value.email);
      setValue('fullname', value.fullname);
      setValue('gender', value?.scientificProfile?.gender);
      setValue('academicRank', value?.scientificProfile?.academicRank);
      setValue('academicDegree', value?.scientificProfile?.academicDegree);
      setValue('organizationId', value.organizationId);
      setValue('specificOrganizationName', value?.specificOrganizationName);
      setValue('roleId', '');
      setIsCreateNewMember(false);
    };
    const handleEmailRemove = () => {
      setIsCreateNewMember(true);
      setQueryEmail('');
      reset({
        contact: null,
        fullname: '',
        academicRank: '',
        academicDegree: '',
        organizationId: '',
        roleId: '',
        specificOrganizationName: null,
      });
    };
    const handleEmailBlur = (value) => {
      if (!value?.id) {
        if (contactsData?.data.length) {
          const contact = contactsData?.data.find((e) => e.email === value);

          if (contact) {
            setValue('email', contact?.email);
            setValue('contact', contact);
            setValue('fullname', contact.fullname);
            setValue('gender', contact?.scientificProfile?.gender);
            setValue('academicRank', contact?.scientificProfile?.academicRank);
            setValue('academicDegree', contact?.scientificProfile?.academicDegree);
            setValue('organizationId', contact.organizationId);
            setValue('specificOrganizationName', contact.specificOrganizationName);
            setValue('roleId', '');
            setIsCreateNewMember(false);
          } else {
            setIsCreateNewMember(true);
            reset({
              contact: value,
              fullname: '',
              gender: '',
              academicRank: '',
              academicDegree: '',
              organizationId: '',
              roleId: '',
              specificOrganizationName: null,
            });
          }
        } else {
          setIsCreateNewMember(true);
          reset({
            contact: value,
            fullname: '',
            gender: '',
            academicRank: '',
            academicDegree: '',
            organizationId: '',
            roleId: '',
            specificOrganizationName: null,
          });
        }
      }
    };
    // #endregion Event

    return (
      <Dialog
        header={
          memberControl?.type === 'APPEND' ? t('council.member.add') : t('council.member.update')
        }
        visible={visible}
        onHide={() => setVisible(false)}
        draggable={false}
        className="w-full sm:w-7 lg:w-5"
      >
        <div className="grid p-fluid">
          <div className="col-12">
            <div className="relative mt-1 mb-3">
              <Combobox
                placeholder={t('council.member.placeholderInputEmail')}
                label="Email"
                isRequired
                showClear
                name="contact"
                renderField="email"
                control={control}
                errors={errors}
                items={contacts}
                search={search}
                itemTemplate={(item) => item.email}
                onSearchChange={handleEmailSelect}
                onSearchRemove={handleEmailRemove}
                onBlur={handleEmailBlur}
              />
            </div>
          </div>
          <div className="col-9">
            <TextInput
              name="fullname"
              label={t('council.member.fields.name')}
              isRequired
              control={control}
              errors={errors}
              disabled={!isCreateNewMember}
            />
          </div>

          <div className="col-3">
            <Select
              name="gender"
              label={t('council.member.fields.gender')}
              isRequired
              inputField="value"
              options={genders}
              control={control}
              errors={errors}
            />
          </div>

          <div className="col-6">
            <Select
              label={t('council.member.fields.academicRank')}
              name="academicRank"
              control={control}
              errors={errors}
              options={getAcademicRanks(t)}
              inputField="value"
              disabled={!isCreateNewMember}
            />
          </div>
          <div className="col-6">
            <Select
              label={t('council.member.fields.academicDegree')}
              name="academicDegree"
              control={control}
              errors={errors}
              isRequired
              options={getAcademicDegrees(t)}
              inputField="value"
              disabled={!isCreateNewMember}
            />
          </div>

          <div className="col-12">
            <Select
              name="organizationId"
              label={t('council.member.fields.organization')}
              isRequired
              options={organizations}
              control={control}
              errors={errors}
              disabled={!isCreateNewMember}
            />
          </div>

          {currentOrganization?.englishName === 'Other' && (
            <div className="col-12">
              <TextareaInput
                label={t('council.member.fields.specificOrganizationName')}
                name="specificOrganizationName"
                type="text"
                control={control}
                errors={errors}
                isRequired
                disabled={!isCreateNewMember}
              />
            </div>
          )}

          <div className="col-12">
            <Select
              name="roleId"
              label={t('council.member.fields.role')}
              isRequired
              inputField="value"
              options={roles}
              control={control}
              errors={errors}
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-content-end mt-4">
          <Button
            label={t('formLayout.action.cancel')}
            severity="danger"
            onClick={() => setVisible(false)}
            className="mr-1 w-8rem"
          />
          <Button
            label={
              memberControl?.type === 'APPEND'
                ? t('formLayout.action.add')
                : t('formLayout.action.update')
            }
            severity="info"
            type="submit"
            className="w-8rem"
            onClick={handleAddMember}
            disabled={!Object.keys(dirtyFields).length}
          />
        </div>
      </Dialog>
    );
  }
);

MemberDialog.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  organizations: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  roles: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  curMembers: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  projectProposalMembers: PropTypes.array,
  councilTypeSelected: PropTypes.string,
};

MemberDialog.defaultProps = {
  projectProposalMembers: [],
  organizations: [],
  roles: [],
  curMembers: [],
  councilTypeSelected: undefined,
};

export default MemberDialog;
